import type { Map as MlMap } from 'maplibre-gl'
import { addUserLocationLayers } from './userLocationLayer'
import { addHighlightLayer } from './highlightMarker'

/**
 * Stadia Maps style URL builders and day/night swap helper.
 *
 * Research §Code Examples Pattern 2 (lines 304-336). MapLibre's `setStyle`
 * preserves camera but drops user-added sources/layers — we re-register
 * them on the `style.load` event that fires after the new style commits
 * (Pitfall 1, lines 701-709).
 *
 * Decisions:
 *   - D-62: Stadia Maps free non-commercial tier
 *   - D-63: Alidade Smooth ↔ Alidade Smooth Dark style pair
 *   - D-64: setStyle + style.load re-registration
 *   - D-65: VITE_STADIA_MAPS_API_KEY in browser bundle (domain-restricted)
 */

export const STYLE_DAY = (apiKey: string): string =>
  `https://tiles.stadiamaps.com/styles/alidade_smooth.json?api_key=${apiKey}`

export const STYLE_NIGHT = (apiKey: string): string =>
  `https://tiles.stadiamaps.com/styles/alidade_smooth_dark.json?api_key=${apiKey}`

/**
 * Swap the map's basemap style and re-register our user-location +
 * highlight-marker layers on the new style.
 *
 * The `style.load` handler is one-shot — it fires once after the new style
 * commits, re-adds our layers (both `addUserLocationLayers` and
 * `addHighlightLayer` are idempotent), and then removes itself.
 */
export function swapTheme(
  map: MlMap,
  nextStyleUrl: string,
  onStyleReady?: () => void,
): void {
  const handler = () => {
    addUserLocationLayers(map)
    addHighlightLayer(map)
    map.off('style.load', handler)
    onStyleReady?.()
  }
  map.on('style.load', handler)
  map.setStyle(nextStyleUrl)
}
